<template>
  <div class="m-t-lg">
    <div class="row" v-show="cropping">
      <div class="col-xs-12 col-sm-12 col-md-6">
        <div class="form-group float-label-control">
          <label for="">{{ labelText }}</label>

          <input type="text" class="form-control" :placeholder="labelText" :value="fileName" disabled v-float-label>
        </div>
      </div>
      <div class="col-xs-12 col-sm-12 col-md-6">
        <label class="btn btn-primary btn-sm btn-block" :class="{ 'disabled' : $user.isDisabled }" :for="`photo_input_${name}`">
          <input
            type="file"
            class="sr-only"
            :disabled="$user.isDisabled"
            :id="`photo_input_${name}`"
            name="file"
            accept=".jpg,.jpeg,.png,.gif"
            @change="handleFileSelection"
            @input="handleFileSelection">
          Select Photo
        </label>
      </div>
    </div>

    <div class="crop-container-outer m-t-sm m-b-sm" v-show="cropping && imageIsSelected">
      <div class="crop-container">
        <img v-if="haveNoExistingImage" :id="`crop-image-${name}`">
        <img v-if="haveExistingImage" :id="`crop-image-${name}`" :src="originalDataUri">
      </div>

      <div class="m-t-sm">
        <ladda-button :on-click="handleSaveCrop" :max-width="true" size="xsmall" type="info">Save Crop</ladda-button>
      </div>
    </div>

    <div class="crop-preview" v-show="notCropping">
      <a @click="handleEditCrop" v-show="$user.isEnabled" class="btn btn-info btn-edit-profile"><i class="fa fa-pencil disabled"></i></a>

      <div class="preview">
        <img v-if="preview" :src="preview" :class="className">
        <img v-else-if="haveExistingImage" :class="className" :src="existingPreviewPath">
      </div>
    </div>
  </div>
</template>

<script>
  import LaddaButton from 'components/forms/LaddaButton'
  import { notify } from 'utils'
  import Cropper from 'cropperjs'
  import moment from 'moment'

  export default {
    props: {
      photoStoragePath: {
        required: true
      },
      originalDataUri: {
        required: false
      },
      originalName: {
        required: false
      },
      name: {
        required: true,
        type: String
      },
      labelText: {
        required: true,
        type: String
      },
      className: {
        required: true,
        type: String
      }
    },

    components: {
      LaddaButton
    },

    data () {
      return {
        cropping: true,
        file: null,
        image: null,
        preview: null,
        cropper: {
          instance: null,
          data: {}
        }
      }
    },

    mounted () {
      this.image = document.getElementById(`crop-image-${this.name}`)

      if (this.haveExistingImage) {
        this.cropping = false
      } else {
        this.cropping = true
      }
    },

    watch: {
      /**
       * Watch the photo storage path property, when it changes we will check
       * if we have an existing image and if we do we'll disable cropping as
       * we will want to show the preview by default.
       *
       * @return void
       */
      photoStoragePath () {
        if (this.haveExistingImage) {
          this.cropping = false
        } else {
          this.cropping = true
        }
      }
    },

    computed: {
      /**
       * Determine if we have an image selected, either as an existing image or one
       * that has been uploaded.
       *
       * @return boolean
       */
      imageIsSelected () {
        return this.haveExistingImage || this.file !== null
      },

      /**
       * Determine if we have an existing image uploaded.
       *
       * @return boolean
       */
      haveExistingImage () {
        return this.photoStoragePath !== null
      },

      /**
       * Determine if we have no existing image uploaded.
       *
       * @return boolean
       */
      haveNoExistingImage () {
        return this.photoStoragePath === null
      },

      /**
       * Get the existing photos preview path with an appended timestamp to bypass
       * any browser caching.
       *
       * @return string
       */
      existingPreviewPath () {
        return `${this.photoStoragePath}/256x256.jpg?${moment().unix()}`
      },

      /**
       * Determine if we are not cropping.
       *
       * @return boolean
       */
      notCropping () {
        return !this.cropping
      },

      /**
       * Get the file name.
       *
       * @return string|null
       */
      fileName () {
        if (this.file && this.file.name) {
          return this.file.name
        }

        return null
      }
    },

    methods: {
      /**
       * Handle changes to the selected file.
       *
       * @param object files
       *
       * @return void
       */
      handleFileSelection ({ target: { files } }) {
        if (files.length === 0) {
          this.file = null

          return
        }

        this.file = files.item(0)

        if (!/^image\/\w+/.test(this.file.type)) {
          this.file = null

          notify.error('Please choose an image file.')

          return
        }

        this.image.src = URL.createObjectURL(this.file)

        this.$nextTick(this.bindCropperToImage)
      },

      /**
       * Handle saving the crop by grabbing the cropped canvas' data URL.
       *
       * This emits the file object up to the parent as well as the cropped data.
       *
       * @return Promise
       */
      handleSaveCrop () {
        return new Promise((resolve) => {
          setTimeout(() => {
            this.preview = this.cropper.instance.getCroppedCanvas().toDataURL()

            this.cropping = false

            this.$emit('input', { file: this.file, crop: this.cropper.data })

            resolve()
          }, 500)
        })
      },

      /**
       * Handle editing an uploaded photo.
       *
       * @return void
       */
      handleEditCrop () {
        this.cropping = true

        this.image = document.getElementById(`crop-image-${this.name}`)

        // If we don't have a file object but we do have an existing image then we need
        // to convert the existing original data URI to a file object. We also pass
        // along the original photo name to populate the input box.
        if (!this.file && this.haveExistingImage) {
          this.file = this.convertDataUriToFile(this.originalDataUri, this.originalName)
        }

        this.$nextTick(this.bindCropperToImage)
      },

      /**
       * Bind a cropper instance to the image.
       *
       * @return void
       */
      bindCropperToImage () {
        if (this.cropper.instance) {
          this.cropper.instance.destroy()
        }

        this.cropper.instance = new Cropper(this.image, {
          viewMode: 3,
          aspectRatio: 1,
          crop: event => {
            this.cropper.data = event.detail
          }
        })
      },

      /**
       * Convert a data URI to a File object.
       *
       * Taken from SO: <https://stackoverflow.com/a/12300351/280329>
       *
       * @param string uri
       *
       * @return File
       */
      convertDataUriToFile (uri, name) {
        let byteString = atob(uri.split(',')[1])
        let mimeString = uri.split(',')[0].split(':')[1].split(';')[0]
        let ab = new ArrayBuffer(byteString.length)
        let ia = new Uint8Array(ab)

        for (var i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i)
        }

        return new File([new Blob([ab], { type: mimeString })], name || 'original.jpg')
      }
    }
  }
</script>



// WEBPACK FOOTER //
// PhotoCropper.vue?056e728e